import axios, { AxiosError, AxiosInstance } from "axios";

import { translateError } from "@/i18n/runtime";
import { ApiError, ApiErrorKind, ApiFieldError, MobileAuthResponse } from "../types";
import {
  actionFailureMessage,
  actionSuccessMessage,
  dispatchActionNotification,
  isActionRequest,
} from "./actionNotifications";
import {
  clearMobileRefreshCredential,
  getMobileRefreshCredential,
  isNativeApp,
  setMobileRefreshCredential,
} from "./mobileAuth";

// Development requests must stay browser-relative so Vite can proxy them.
// Docker-only hostnames such as `backend` are resolved by Vite on the server
// side and must never be sent to the browser.
const baseURL = import.meta.env.DEV
  ? "/api/v1"
  : import.meta.env.VITE_API_BASE_URL || "/api/v1";

const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 10000,
  withCredentials: true,
});

const authenticationExpiredEvent = "celsus:authentication-expired";
let accessToken: string | null = null;
let refreshPromise: Promise<string> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function isApiError(value: unknown): value is ApiError {
  if (!isRecord(value) || typeof value.kind !== "string") return false;
  return typeof value.message === "string" && Array.isArray(value.fields);
}

function statusKind(status: number | undefined): ApiErrorKind {
  if (status === 401) return "authentication";
  if (status === 403) return "authorization";
  if (status === 404) return "not_found";
  if (status === 409) return "conflict";
  if (status === 422) return "validation";
  if (status === 429) return "rate_limit";
  if (status !== undefined && status >= 500) return "server";
  return status === undefined ? "network" : "unknown";
}

function fieldErrors(detail: unknown): ApiFieldError[] {
  if (!Array.isArray(detail)) return [];
  return detail.flatMap((item: unknown): ApiFieldError[] => {
    if (!isRecord(item) || typeof item.msg !== "string") return [];
    const location = Array.isArray(item.loc) ? item.loc : [];
    return [
      {
        path: location.map(String).filter((part: string) => part !== "body"),
        message: item.msg,
        ...(typeof item.type === "string" ? { code: item.type } : {}),
        ...(isRecord(item.params)
          ? {
              params: Object.fromEntries(
                Object.entries(item.params).filter(
                  ([, value]) =>
                    typeof value === "string" ||
                    typeof value === "number" ||
                    typeof value === "boolean"
                )
              ) as Record<string, string | number | boolean>,
            }
          : {}),
      },
    ];
  });
}

function isUnsafeTechnicalMessage(message: string): boolean {
  return /traceback|stack trace|\bat .+\(.+:\d+:\d+\)|sqlalchemy\.|asyncpg\.|file ".+", line \d+/i.test(
    message
  );
}

export function normalizeApiError(error: unknown): ApiError {
  if (isApiError(error)) return error;
  if (!axios.isAxiosError(error)) {
    return {
      kind: "unknown",
      message: error instanceof Error ? error.message : "An unexpected error occurred",
      fields: [],
    };
  }
  const axiosError = error as AxiosError<unknown>;
  const status = axiosError.response?.status;
  const body = axiosError.response?.data;
  const detail = isRecord(body) && "detail" in body ? body.detail : body;
  const fields = fieldErrors(detail);
  const objectDetail = isRecord(detail) ? detail : undefined;
  const rawMessage =
    typeof detail === "string"
      ? detail
      : typeof objectDetail?.message === "string"
        ? objectDetail.message
        : fields.length > 0
          ? fields.map((field: ApiFieldError) => field.message).join("; ")
          : axiosError.message || "Request failed";
  const message =
    statusKind(status) === "server" || isUnsafeTechnicalMessage(rawMessage)
      ? translateError("unexpected_error", "Something went wrong. Please try again.")
      : typeof objectDetail?.code === "string"
        ? translateError(
            objectDetail.code,
            rawMessage,
            isRecord(objectDetail.params)
              ? (objectDetail.params as Record<string, string | number | boolean>)
              : undefined
          )
        : rawMessage;
  const requestId = axiosError.response?.headers["x-request-id"];
  return {
    kind: statusKind(status),
    ...(status === undefined ? {} : { status }),
    message,
    fields,
    ...(typeof requestId === "string" ? { requestId } : {}),
    ...(typeof objectDetail?.code === "string" ? { code: objectDetail.code } : {}),
    ...(isRecord(objectDetail?.params)
      ? {
          params: objectDetail.params as Record<string, string | number | boolean>,
        }
      : {}),
  };
}

export function onAuthenticationExpired(listener: () => void): () => void {
  window.addEventListener(authenticationExpiredEvent, listener);
  return () => window.removeEventListener(authenticationExpiredEvent, listener);
}

api.interceptors.request.use((config) => {
  if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
  return config;
});

async function refreshAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = (async (): Promise<string> => {
      if (isNativeApp()) {
        const refreshCredential = await getMobileRefreshCredential();
        if (!refreshCredential) throw new Error("Mobile session is unavailable");
        try {
          const response = await axios.post<MobileAuthResponse>(
            `${baseURL}/auth/mobile/refresh`,
            { refresh_token: refreshCredential }
          );
          await setMobileRefreshCredential(response.data.refresh_token);
          setAccessToken(response.data.access_token);
          return response.data.access_token;
        } catch (error: unknown) {
          await clearMobileRefreshCredential();
          throw error;
        }
      }
      const response = await axios.post<{ access_token: string }>(
        `${baseURL}/auth/refresh`,
        undefined,
        { withCredentials: true }
      );
      setAccessToken(response.data.access_token);
      return response.data.access_token;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export async function restoreAccessToken(): Promise<void> {
  await refreshAccessToken();
}

// Add response interceptor to handle errors
api.interceptors.response.use(
  (response) => {
    const message = isActionRequest(response.config.method)
      ? actionSuccessMessage(response.config.url, response.config.method)
      : null;
    if (message) dispatchActionNotification({ message, tone: "success" });
    return response;
  },
  async (error: unknown) => {
    if (axios.isAxiosError(error)) {
      const request = error.config as
        (typeof error.config & { _celsusRetried?: boolean }) | undefined;
      const isAuthEndpoint = request?.url?.includes("/auth/") ?? false;
      if (
        error.response?.status === 401 &&
        request &&
        !request._celsusRetried &&
        !isAuthEndpoint
      ) {
        request._celsusRetried = true;
        try {
          const token = await refreshAccessToken();
          request.headers.Authorization = `Bearer ${token}`;
          return api.request(request);
        } catch {
          setAccessToken(null);
        }
      }
    }
    const normalized = normalizeApiError(error);
    if (
      axios.isAxiosError(error) &&
      isActionRequest(error.config?.method) &&
      actionSuccessMessage(error.config?.url, error.config?.method) !== null
    ) {
      const action = actionSuccessMessage(error.config?.url, error.config?.method);
      if (action) {
        dispatchActionNotification({
          message: actionFailureMessage(action, normalized.message),
          tone: "error",
        });
      }
    }
    if (normalized.kind === "authentication") {
      setAccessToken(null);
      window.dispatchEvent(new Event(authenticationExpiredEvent));
    }
    return Promise.reject(normalized);
  }
);

export default api;

// Helper function to extract error message
export function getErrorMessage(error: unknown): string {
  return isRecord(error) && typeof error.message === "string"
    ? error.message
    : normalizeApiError(error).message;
}
